import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type {
  SymbolsResponse,
  OHLCVCandle,
  SymbolPrediction,
  Alert,
  MarketType,
  Interval,
  RegimeResult,
  IndicatorsResult,
  CalendarResponse,
  CurrencyStrengthResult,
  SentimentResult,
  SymbolEnsemblePrediction,
  StrategistResult,
  TradeRecommendation,
} from '../types'
import {
  fetchSymbols,
  fetchSymbolsMetadata,
  fetchOHLCV,
  fetchPrediction,
  fetchPredictionEnsemble,
  fetchAlerts,
  fetchStatus,
  fetchRegime,
  fetchIndicators,
  fetchEconomicCalendar,
  fetchCurrencyStrength,
  fetchSentiment,
  fetchStrategist,
  fetchActiveSignal,
} from '../api/client'

interface StoreState {
  // Symbol data
  symbols: SymbolsResponse | null
  selectedSymbol: string | null
  selectedMarket: MarketType
  selectedInterval: Interval

  // Predictions cache: symbol -> SymbolPrediction
  predictions: Record<string, SymbolPrediction>
  loadingPredictions: Record<string, boolean>

  // Ensemble cache: symbol -> SymbolEnsemblePrediction
  ensemblePredictions: Record<string, SymbolEnsemblePrediction>
  loadingEnsemble: Record<string, boolean>

  // OHLCV cache
  ohlcv: Record<string, OHLCVCandle[]>

  // Alerts
  alerts: Alert[]
  dismissedAlertIds: Set<string>


  // Status
  kronosReady: boolean
  kronosDevice: string

  // Analysis
  regimes:          Record<string, RegimeResult>
  indicators:       Record<string, IndicatorsResult>
  calendar:         CalendarResponse | null
  calendarLoading:  boolean
  currencyStrength: CurrencyStrengthResult | null
  sentiment:        Record<string, SentimentResult>
  sentimentLoading: boolean
  strategist:       Record<string, StrategistResult>
  strategistLoading: boolean
  // 実際の売買シグナル(ADX/RSI/MACDルールフィルタ通過済み、AI軍師の自己申告とは別物)
  activeSignal:        Record<string, TradeRecommendation>
  activeSignalLoading: boolean

  // Navigation
  activeTab: 'dashboard' | 'analysis'

  // Error tracking
  errors: Record<string, string | null>

  // Metadata cache: symbol -> human-readable name
  metadata: Record<string, string>

  // Actions
  setSelectedSymbol: (symbol: string) => void
  setSelectedMarket: (market: MarketType) => void
  setSelectedInterval: (interval: Interval) => void
  setActiveTab: (tab: 'dashboard' | 'analysis') => void
  loadSymbols: () => Promise<void>
  loadMetadata: () => Promise<void>
  loadPrediction: (symbol: string, interval?: Interval) => Promise<void>
  loadEnsemblePrediction: (symbol: string) => Promise<void>
  loadOHLCV: (symbol: string, interval?: Interval) => Promise<void>
  loadAlerts: () => Promise<void>
  dismissAlert: (id: string) => void
  dismissAllAlerts: () => void

  loadStatus: () => Promise<void>
  loadRegime: (symbol: string) => Promise<void>
  loadIndicators: (symbol: string, interval?: Interval) => Promise<void>
  loadCalendar: (hours?: number) => Promise<void>
  loadCurrencyStrength: () => Promise<void>
  loadSentiment: (symbol: string) => Promise<void>
  loadStrategist: (symbol: string) => Promise<void>
  loadActiveSignal: (symbol: string) => Promise<void>
  clearError: (key: string) => void
}

const LS_DISMISSED_KEY = 'tvista_dismissed_alert_ids'
const LS_NAV_KEY       = 'tvista_nav'

function loadDismissedFromStorage(): Set<string> {
  try {
    const raw = localStorage.getItem(LS_DISMISSED_KEY)
    if (!raw) return new Set()
    return new Set(JSON.parse(raw) as string[])
  } catch {
    return new Set()
  }
}

function saveDismissedToStorage(ids: Set<string>) {
  try {
    localStorage.setItem(LS_DISMISSED_KEY, JSON.stringify([...ids]))
  } catch { /* ignore quota errors */ }
}

interface PersistedNav {
  selectedSymbol:   string | null
  selectedInterval: string
  activeTab:        string
  selectedMarket:   string
}

function loadNavFromStorage(): Partial<PersistedNav> {
  try {
    const raw = localStorage.getItem(LS_NAV_KEY)
    return raw ? (JSON.parse(raw) as PersistedNav) : {}
  } catch {
    return {}
  }
}

function saveNavToStorage(patch: Partial<PersistedNav>) {
  try {
    const current = loadNavFromStorage()
    localStorage.setItem(LS_NAV_KEY, JSON.stringify({ ...current, ...patch }))
  } catch { /* ignore quota errors */ }
}

export const useStore = create<StoreState>()(
  devtools(
    (set, get) => {
      const _nav = loadNavFromStorage()
      return ({
      // ── Initial state ──────────────────────────────────────────────────

      symbols: null,
      selectedSymbol:   _nav.selectedSymbol   ?? null,
      selectedMarket:   (_nav.selectedMarket  as MarketType) ?? 'all',
      selectedInterval: (_nav.selectedInterval as Interval)  ?? '1d',

      predictions: {},
      loadingPredictions: {},
      ensemblePredictions: {},
      loadingEnsemble: {},

      ohlcv: {},

      alerts: [],
      dismissedAlertIds: loadDismissedFromStorage(),

      kronosReady: false,
      kronosDevice: 'unknown',

      regimes:          {},
      indicators:       {},
      calendar:         null,
      calendarLoading:  false,
      currencyStrength: null,
      sentiment:        {},
      sentimentLoading: false,
      strategist:       {},
      strategistLoading: false,
      activeSignal:        {},
      activeSignalLoading: false,

      activeTab:      (_nav.activeTab      as 'dashboard' | 'analysis') ?? 'dashboard',

      errors: {},
      metadata: {},

      // ── Actions ────────────────────────────────────────────────────────

      setSelectedSymbol: (symbol) => {
        saveNavToStorage({ selectedSymbol: symbol || null })
        set({ selectedSymbol: symbol || null }, false, 'setSelectedSymbol')
        if (!symbol) return   // back navigation — no API calls needed
        const state = get()
        // Load all 3 timeframes so the multi-timeframe panel can compare them
        for (const iv of ['1h', '4h', '1d'] as Interval[]) {
          if (!state.predictions[`${symbol}_${iv}`]) {
            state.loadPrediction(symbol, iv)
          }
          // Pre-load OHLCV for all intervals so switching tabs shows correct candles immediately
          const ohlcvKey = `${symbol}_${iv}`
          if (!state.ohlcv[ohlcvKey]) {
            state.loadOHLCV(symbol, iv)
          }
        }
      },

      setSelectedMarket: (market) => {
        saveNavToStorage({ selectedMarket: market })
        set({ selectedMarket: market }, false, 'setSelectedMarket')
      },

      setSelectedInterval: (interval) => {
        saveNavToStorage({ selectedInterval: interval })
        set({ selectedInterval: interval }, false, 'setSelectedInterval')
        const state = get()

        // Load predictions for ALL known symbols at the new interval so the
        // sidebar signal bars update immediately without requiring a click.
        // Backend serves these from its scheduler cache, so they return fast.
        const allSymbols = [
          ...(state.symbols?.crypto ?? []),
          ...(state.symbols?.stock ?? []),
          ...(state.symbols?.fx   ?? []),
        ]
        for (const sym of allSymbols) {
          const key = `${sym}_${interval}`
          if (!state.predictions[key] && !state.loadingPredictions[key]) {
            state.loadPrediction(sym, interval)
          }
        }

        if (state.selectedSymbol) {
          const ohlcvKey = `${state.selectedSymbol}_${interval}`
          if (!state.ohlcv[ohlcvKey]) {
            state.loadOHLCV(state.selectedSymbol, interval)
          }
        }
      },

      setActiveTab: (tab) => {
        saveNavToStorage({ activeTab: tab })
        set({ activeTab: tab }, false, 'setActiveTab')
      },

      loadSymbols: async () => {
        try {
          const symbols = await fetchSymbols()
          set({ symbols, errors: { ...get().errors, symbols: null } }, false, 'loadSymbols/success')

          // Dynamic load of metadata
          await get().loadMetadata()

          // Pre-load ensemble predictions for all symbols so the sidebar
          // signal bars are populated on first render without any clicks.
          const allSymbols = [...symbols.crypto, ...symbols.stock, ...symbols.fx]
          for (const sym of allSymbols) {
            const key = sym.toUpperCase()
            if (!get().ensemblePredictions[key] && !get().loadingEnsemble[key]) {
              get().loadEnsemblePrediction(sym)
            }
          }

          // 復元したシンボルが存在しない場合は先頭を自動選択
          const { selectedSymbol } = get()
          if (!selectedSymbol || !allSymbols.includes(selectedSymbol)) {
            const first = symbols.crypto[0] ?? symbols.stock[0] ?? symbols.fx[0] ?? null
            if (first) get().setSelectedSymbol(first)
          }
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Failed to load symbols'
          set(
            { errors: { ...get().errors, symbols: message } },
            false,
            'loadSymbols/error',
          )
        }
      },

      loadMetadata: async () => {
        try {
          const metadata = await fetchSymbolsMetadata()
          set({ metadata, errors: { ...get().errors, metadata: null } }, false, 'loadMetadata/success')
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Failed to load metadata'
          set(
            { errors: { ...get().errors, metadata: message } },
            false,
            'loadMetadata/error',
          )
        }
      },

      loadPrediction: async (symbol, interval) => {
        const effectiveInterval = interval ?? get().selectedInterval
        const cacheKey = `${symbol}_${effectiveInterval}`
        if (get().loadingPredictions[cacheKey]) return

        set(
          (state) => ({
            loadingPredictions: { ...state.loadingPredictions, [cacheKey]: true },
            errors: { ...state.errors, [`prediction_${cacheKey}`]: null },
          }),
          false,
          'loadPrediction/start',
        )

        try {
          const prediction = await fetchPrediction(symbol, effectiveInterval)
          set(
            (state) => ({
              predictions: { ...state.predictions, [cacheKey]: prediction },
              loadingPredictions: { ...state.loadingPredictions, [cacheKey]: false },
            }),
            false,
            'loadPrediction/success',
          )
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Failed to load prediction'
          set(
            (state) => ({
              loadingPredictions: { ...state.loadingPredictions, [cacheKey]: false },
              errors: { ...state.errors, [`prediction_${cacheKey}`]: message },
            }),
            false,
            'loadPrediction/error',
          )
        }
      },


      loadEnsemblePrediction: async (symbol) => {
        const cacheKey = symbol.toUpperCase()
        if (get().loadingEnsemble[cacheKey]) return

        set(
          (state) => ({
            loadingEnsemble: { ...state.loadingEnsemble, [cacheKey]: true },
            errors: { ...state.errors, [`ensemble_${cacheKey}`]: null },
          }),
          false,
          'loadEnsemblePrediction/start',
        )

        try {
          const ensemble = await fetchPredictionEnsemble(symbol)
          set(
            (state) => ({
              ensemblePredictions: { ...state.ensemblePredictions, [cacheKey]: ensemble },
              loadingEnsemble: { ...state.loadingEnsemble, [cacheKey]: false },
            }),
            false,
            'loadEnsemblePrediction/success',
          )
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Failed to load ensemble prediction'
          set(
            (state) => ({
              loadingEnsemble: { ...state.loadingEnsemble, [cacheKey]: false },
              errors: { ...state.errors, [`ensemble_${cacheKey}`]: message },
            }),
            false,
            'loadEnsemblePrediction/error',
          )
        }
      },

      loadOHLCV: async (symbol, interval) => {
        const effectiveInterval = interval ?? get().selectedInterval
        const cacheKey = `${symbol}_${effectiveInterval}`

        // Each interval fetches its own candle data at the correct timeframe
        const fetchLimit = effectiveInterval === '1d' ? 100 : 200

        try {
          const result = await fetchOHLCV(symbol, effectiveInterval, fetchLimit)
          set(
            (state) => ({
              ohlcv: { ...state.ohlcv, [cacheKey]: result.data },
              errors: { ...state.errors, [`ohlcv_${cacheKey}`]: null },
            }),
            false,
            'loadOHLCV/success',
          )
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Failed to load OHLCV'
          set(
            (state) => ({
              errors: { ...state.errors, [`ohlcv_${cacheKey}`]: message },
            }),
            false,
            'loadOHLCV/error',
          )
        }
      },

      loadAlerts: async () => {
        try {
          const { alerts } = await fetchAlerts(50)
          set({ alerts, errors: { ...get().errors, alerts: null } }, false, 'loadAlerts/success')
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Failed to load alerts'
          set(
            { errors: { ...get().errors, alerts: message } },
            false,
            'loadAlerts/error',
          )
        }
      },

      dismissAlert: (id) => {
        const next = new Set([...get().dismissedAlertIds, id])
        saveDismissedToStorage(next)
        set({ dismissedAlertIds: next }, false, 'dismissAlert')
      },

      dismissAllAlerts: () => {
        const next = new Set(get().alerts.map((a) => a.id))
        saveDismissedToStorage(next)
        set({ dismissedAlertIds: next }, false, 'dismissAllAlerts')
      },



      loadStatus: async () => {
        try {
          const status = await fetchStatus()
          set(
            {
              kronosReady: status.kronos_ready,
              kronosDevice: status.device,
              errors: { ...get().errors, status: null },
            },
            false,
            'loadStatus/success',
          )
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Failed to load status'
          set(
            {
              kronosReady: false,
              errors: { ...get().errors, status: message },
            },
            false,
            'loadStatus/error',
          )
        }
      },

      loadRegime: async (symbol) => {
        try {
          const result = await fetchRegime(symbol)
          set(
            (state) => ({ regimes: { ...state.regimes, [symbol]: result } }),
            false, 'loadRegime/success',
          )
        } catch (err) {
          const message = err instanceof Error ? err.message : 'レジーム取得失敗'
          set(
            (state) => ({ errors: { ...state.errors, [`regime_${symbol}`]: message } }),
            false, 'loadRegime/error',
          )
        }
      },

      loadIndicators: async (symbol, interval) => {
        const iv = interval ?? get().selectedInterval
        const key = `${symbol}_${iv}`
        try {
          const result = await fetchIndicators(symbol, iv)
          set(
            (state) => ({ indicators: { ...state.indicators, [key]: result } }),
            false, 'loadIndicators/success',
          )
        } catch (err) {
          const message = err instanceof Error ? err.message : '指標取得失敗'
          set(
            (state) => ({ errors: { ...state.errors, [`indicators_${key}`]: message } }),
            false, 'loadIndicators/error',
          )
        }
      },

      loadCalendar: async (hours = 24) => {
        set({ calendarLoading: true }, false, 'loadCalendar/start')
        try {
          const result = await fetchEconomicCalendar(hours)
          set(
            { calendar: result, calendarLoading: false, errors: { ...get().errors, calendar: null } },
            false, 'loadCalendar/success',
          )
        } catch (err) {
          const message = err instanceof Error ? err.message : 'カレンダー取得失敗'
          set(
            { calendarLoading: false, errors: { ...get().errors, calendar: message } },
            false, 'loadCalendar/error',
          )
        }
      },

      loadCurrencyStrength: async () => {
        try {
          const result = await fetchCurrencyStrength()
          set({ currencyStrength: result }, false, 'loadCurrencyStrength/success')
        } catch (err) {
          const message = err instanceof Error ? err.message : '通貨強度取得失敗'
          set(
            (state) => ({ errors: { ...state.errors, currencyStrength: message } }),
            false, 'loadCurrencyStrength/error',
          )
        }
      },

      loadSentiment: async (symbol) => {
        set({ sentimentLoading: true }, false, 'loadSentiment/start')
        try {
          const result = await fetchSentiment(symbol)
          set(
            (state) => ({
              sentiment:        { ...state.sentiment, [symbol]: result },
              sentimentLoading: false,
              errors:           { ...state.errors, [`sentiment_${symbol}`]: null },
            }),
            false, 'loadSentiment/success',
          )
        } catch (err) {
          const message = err instanceof Error ? err.message : 'センチメント取得失敗'
          set(
            (state) => ({
              sentimentLoading: false,
              errors:           { ...state.errors, [`sentiment_${symbol}`]: message },
            }),
            false, 'loadSentiment/error',
          )
        }
      },

      loadStrategist: async (symbol) => {
        set({ strategistLoading: true }, false, 'loadStrategist/start')
        try {
          const result = await fetchStrategist(symbol)
          set(
            (state) => ({
              strategist:        { ...state.strategist, [symbol]: result },
              strategistLoading: false,
              errors:           { ...state.errors, [`strategist_${symbol}`]: null },
            }),
            false, 'loadStrategist/success',
          )
        } catch (err) {
          const message = err instanceof Error ? err.message : 'AIアドバイザーデータ取得失敗'
          set(
            (state) => ({
              strategistLoading: false,
              errors:           { ...state.errors, [`strategist_${symbol}`]: message },
            }),
            false, 'loadStrategist/error',
          )
        }
      },

      loadActiveSignal: async (symbol) => {
        set({ activeSignalLoading: true }, false, 'loadActiveSignal/start')
        try {
          const result = await fetchActiveSignal(symbol)
          set(
            (state) => ({
              activeSignal:        { ...state.activeSignal, [symbol]: result },
              activeSignalLoading: false,
              errors:             { ...state.errors, [`activeSignal_${symbol}`]: null },
            }),
            false, 'loadActiveSignal/success',
          )
        } catch (err) {
          const message = err instanceof Error ? err.message : '実シグナル取得失敗'
          set(
            (state) => ({
              activeSignalLoading: false,
              errors:             { ...state.errors, [`activeSignal_${symbol}`]: message },
            }),
            false, 'loadActiveSignal/error',
          )
        }
      },

      clearError: (key) =>
        set(
          (state) => ({ errors: { ...state.errors, [key]: null } }),
          false,
          'clearError',
        ),
    })}
    , { name: 'KronosStore' },
  ),
)

// ── Selector helpers ─────────────────────────────────────────────────────────

export const selectCurrentPrediction = (state: StoreState) => {
  const key = `${state.selectedSymbol}_${state.selectedInterval}`
  return state.selectedSymbol ? state.predictions[key] ?? null : null
}

export const selectCurrentOHLCV = (state: StoreState) => {
  const key = `${state.selectedSymbol}_${state.selectedInterval}`
  return state.selectedSymbol ? state.ohlcv[key] ?? [] : []
}

export const selectIsPredictionLoading = (state: StoreState) => {
  const key = `${state.selectedSymbol}_${state.selectedInterval}`
  return state.selectedSymbol
    ? state.loadingPredictions[key] ?? false
    : false
}

export const selectFilteredSymbols = (state: StoreState): string[] => {
  if (!state.symbols) return []
  const { crypto, stock, fx } = state.symbols
  switch (state.selectedMarket) {
    case 'crypto':
      return crypto
    case 'stock':
      return stock
    case 'fx':
      return fx
    default:
      return [...crypto, ...stock, ...fx]
  }
}
